import React, { useState, useEffect, useRef, useCallback } from 'react';
import { jobsAPI } from '../../utils/api';

/**
 * Professional Mention Editor Component
 * Implements Facebook/LinkedIn-style mentions with immutable chips
 * 
 * KEY DESIGN: Selected users are tracked separately in state, not extracted from DOM
 * This ensures exact user matching even when multiple users have similar names
 */
const MentionEditor = ({ value, onChange, placeholder, onMentionsChange }) => {
  const editorRef = useRef(null);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [showMentions, setShowMentions] = useState(false);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionPosition, setMentionPosition] = useState(null); // eslint-disable-line no-unused-vars
  const [selectedMentions, setSelectedMentions] = useState([]); // Track selected user objects (source of truth)
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const isInsertingMentionRef = useRef(false); // Flag to prevent handleInput from clearing mentions during insertion
  const mentionChangeTimeoutRef = useRef(null); // Debounce mention change notifications
  const currentMentionQueryRef = useRef(''); // Store current query when dropdown is shown (for reliable replacement)

  // Debounced mention change notification to reduce excessive re-renders
  const notifyMentionsChange = useCallback((mentions) => {
    if (!onMentionsChange) return;
    
    // Clear existing timeout
    if (mentionChangeTimeoutRef.current) {
      clearTimeout(mentionChangeTimeoutRef.current);
    }
    
    // Debounce notifications by 100ms to batch rapid changes
    mentionChangeTimeoutRef.current = setTimeout(() => {
      onMentionsChange(mentions);
      mentionChangeTimeoutRef.current = null;
    }, 100);
  }, [onMentionsChange]);

  // Extract plain text from contentEditable (for display/backend)
  const extractText = useCallback(() => {
    if (!editorRef.current) return '';
    
    // First pass: Collect unique mention chips (deduplicate by email)
    const chips = Array.from(editorRef.current.querySelectorAll('.mention-chip'));
    const uniqueChips = new Map(); // email -> chip
    const chipQueries = new Set(); // Track queries that have been converted to chips
    
    chips.forEach(chip => {
      const userEmail = chip.getAttribute('data-user-email') || '';
      const userName = chip.getAttribute('data-user-name') || '';
      
      if (userEmail && !uniqueChips.has(userEmail)) {
        uniqueChips.set(userEmail, chip);
        // Track possible query variations that would match this chip
        if (userName) {
          chipQueries.add(userName.toLowerCase());
          chipQueries.add(userName.split(' ')[0]?.toLowerCase()); // First name
        }
        const emailUser = userEmail.split('@')[0]?.toLowerCase();
        if (emailUser) chipQueries.add(emailUser);
      }
    });
    
    // Second pass: Extract text, skipping text that matches chip queries
    let text = '';
    const walker = document.createTreeWalker(
      editorRef.current,
      NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
      {
        acceptNode: (node) => {
          // Skip text inside mention chips
          if (node.nodeType === Node.TEXT_NODE && 
              node.parentElement && 
              node.parentElement.classList.contains('mention-chip')) {
            return NodeFilter.FILTER_REJECT;
          }
          return NodeFilter.FILTER_ACCEPT;
        }
      }
    );
    
    let node;
    while ((node = walker.nextNode())) {
      if (node.nodeType === Node.TEXT_NODE) {
        let textContent = node.textContent || '';
        
        // Remove any @query patterns that match existing chips (duplicate cleanup)
        chipQueries.forEach(query => {
          // Match @query at word boundaries
          const regex = new RegExp(`@${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
          textContent = textContent.replace(regex, '');
        });
        
        text += textContent;
        
      } else if (node.nodeType === Node.ELEMENT_NODE && node.classList.contains('mention-chip')) {
        // Only add unique chips (already deduplicated)
        const userEmail = node.getAttribute('data-user-email') || '';
        if (uniqueChips.get(userEmail) === node) {
          const userName = node.getAttribute('data-user-name') || '';
          text += `@${userName || userEmail.split('@')[0]} `;
        }
        // Skip duplicate chips
      }
    }
    
    // Clean up multiple spaces and trim
    return text.replace(/\s+/g, ' ').trim();
  }, []);

  // Handle input in contentEditable
  const handleInput = (e) => {
    // CRITICAL: If we're in the middle of inserting a mention, skip processing
    // This prevents handleInput from clearing mentions before insertMention completes
    if (isInsertingMentionRef.current) {
      console.log('[MentionEditor] Skipping handleInput - mention insertion in progress');
      // Don't call onMentionsChange or process anything - just return
      return;
    }
    
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    
    const range = selection.getRangeAt(0);
    const cursorNode = range.startContainer;
    
    // Check if cursor is inside a mention chip - move it after
    if (cursorNode.nodeType === Node.TEXT_NODE && cursorNode.parentElement?.classList.contains('mention-chip')) {
      const mentionChip = cursorNode.parentElement;
      const rangeAfter = document.createRange();
      rangeAfter.setStartAfter(mentionChip);
      rangeAfter.collapse(true);
      selection.removeAllRanges();
      selection.addRange(rangeAfter);
      
      const text = extractText();
      onChange(text);
      // Keep selectedMentions unchanged - user is just moving cursor
      // But extract from DOM to ensure we have latest
      const chipsInDOM = Array.from(editorRef.current.querySelectorAll('.mention-chip'));
      const mentionsFromDOM = chipsInDOM.map(chip => {
        const userObjectJson = chip.getAttribute('data-user-object');
        if (userObjectJson) {
          try {
            return JSON.parse(userObjectJson);
          } catch (e) {}
        }
        return null;
      }).filter(Boolean);
      
      // Always prioritize DOM mentions, but fall back to state if DOM is empty
      // This ensures mentions persist even if DOM extraction fails temporarily
      const mentionsToNotify = mentionsFromDOM.length > 0 ? mentionsFromDOM : selectedMentions;
      if (mentionsToNotify.length > 0) {
        notifyMentionsChange(mentionsToNotify);
      }
      return;
    }
    
    // Get text before cursor (excluding mention chips)
    const textBeforeCursor = getTextBeforeCursor();
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      // Extract mention query (from @ to cursor)
      // Only consider text after the last @ that's not part of a mention chip
      const mentionText = textBeforeCursor.substring(lastAtIndex + 1).trim();
      
      // Check if there's whitespace/punctuation - mention is complete
      const hasWhitespaceOrPunct = mentionText.match(/[\s.,!?;:]/);
      
      if (!hasWhitespaceOrPunct && mentionText.length > 0) {
        // We're typing a mention - search for users
        const query = mentionText.trim();
        setMentionQuery(query);
        currentMentionQueryRef.current = query; // Store in ref for reliable access during insertion
        setMentionPosition({ start: lastAtIndex, query });
        
        // Debounce search
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
        
        searchTimeoutRef.current = setTimeout(async () => {
          try {
            // Show loading state
            setMentionSuggestions([]);
            const response = await jobsAPI.searchAzureADUsers(query || '');
            setMentionSuggestions(response.data.users || []);
            if (response.data.users && response.data.users.length > 0) {
              setShowMentions(true);
              setSelectedMentionIndex(0);
            } else {
              setShowMentions(query.length === 0);
            }
          } catch (error) {
            console.error('Failed to search users:', error);
            setMentionSuggestions([]);
            setShowMentions(false);
          }
        }, 500); // Increased debounce to 500ms to reduce API calls
      } else {
        // Mention complete or no @ - hide suggestions
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
    
    // Update text content
    const text = extractText();
    onChange(text);
    
    // CRITICAL: Always preserve selectedMentions when user types after a mention chip
    // Extract mentions from DOM chips as the source of truth (contentEditable can lose React state)
    const chipsInDOM = Array.from(editorRef.current.querySelectorAll('.mention-chip'));
    const mentionsFromDOM = chipsInDOM.map(chip => {
      const userObjectJson = chip.getAttribute('data-user-object');
      if (userObjectJson) {
        try {
          const parsed = JSON.parse(userObjectJson);
          return parsed;
        } catch (e) {
          console.warn('[MentionEditor] Failed to parse user object from chip:', e);
        }
      }
      // Fallback to attributes if data-user-object is missing
      const email = chip.getAttribute('data-user-email');
      const name = chip.getAttribute('data-user-name');
      const id = chip.getAttribute('data-user-id');
      if (email) {
        return {
          email: email,
          name: name || email.split('@')[0],
          id: id,
          _id: id
        };
      }
      return null;
    }).filter(Boolean);
    
    // Use DOM mentions as source of truth - they persist even if React state is lost
    // Merge with selectedMentions state (DOM takes priority for accuracy)
    const finalMentions = [];
    const seenEmails = new Set();
    
    // First add mentions from DOM (most reliable)
    mentionsFromDOM.forEach(domMention => {
      const email = (domMention.email || '').toLowerCase();
      if (email && !seenEmails.has(email)) {
        seenEmails.add(email);
        finalMentions.push(domMention);
      }
    });
    
    // Then add any from state that aren't in DOM (shouldn't happen, but safety net)
    selectedMentions.forEach(stateMention => {
      if (!stateMention) return;
      const email = (stateMention.email || '').toLowerCase();
      if (email && !seenEmails.has(email)) {
        seenEmails.add(email);
        finalMentions.push(stateMention);
      }
    });
    
    // Always update state to match DOM (keep them in sync)
    // Only update if mentions actually changed to avoid unnecessary re-renders
    const mentionsChanged = finalMentions.length !== selectedMentions.length || 
        finalMentions.some((m, i) => (m?.email || '').toLowerCase() !== (selectedMentions[i]?.email || '').toLowerCase());
    
    if (mentionsChanged) {
      console.log('[MentionEditor] Syncing state with DOM:', {
        stateCount: selectedMentions.length,
        domCount: mentionsFromDOM.length,
        finalCount: finalMentions.length,
        stateEmails: selectedMentions.map(m => m.email),
        finalEmails: finalMentions.map(m => m.email)
      });
      setSelectedMentions(finalMentions);
    }
    
    // Always notify parent with current mentions from DOM (source of truth)
    // This ensures mentions persist even when typing after them
    // Only notify if mentions changed OR if we have mentions (to ensure they're preserved)
    if (mentionsChanged || finalMentions.length > 0) {
      notifyMentionsChange(finalMentions);
    }
  };

  // Get text content before cursor (excluding mention chips)
  const getTextBeforeCursor = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return '';
    
    const range = selection.getRangeAt(0);
    
    // Walk backwards from cursor, collecting text but skipping mention chips
    let text = '';
    let currentContainer = range.startContainer;
    let currentOffset = range.startOffset;
    
    // If cursor is in a text node, add text before cursor
    if (currentContainer.nodeType === Node.TEXT_NODE) {
      text = currentContainer.textContent.substring(0, currentOffset);
      currentContainer = currentContainer.previousSibling;
    } else {
      // Find last text node or mention chip before cursor
      currentContainer = currentContainer.childNodes[currentOffset - 1] || null;
    }
    
    // Walk backwards collecting text (skip mention chips)
    while (currentContainer) {
      if (currentContainer.nodeType === Node.TEXT_NODE) {
        text = currentContainer.textContent + text;
      } else if (currentContainer.nodeType === Node.ELEMENT_NODE) {
        if (!currentContainer.classList?.contains('mention-chip')) {
          // For non-chip elements, get their text content
          text = currentContainer.textContent + text;
        }
        // Skip mention chips - don't add their text
      }
      
      currentContainer = currentContainer.previousSibling;
    }
    
    // Fallback to simple toString if we're in a simple text node
    if (!text && range.startContainer.nodeType === Node.TEXT_NODE) {
      const rangeBefore = range.cloneRange();
      rangeBefore.selectNodeContents(editorRef.current);
      rangeBefore.setEnd(range.startContainer, range.startOffset);
      text = rangeBefore.toString();
    }
    
    return text;
  };

  // Insert mention chip at cursor - Facebook/LinkedIn style
  const insertMention = useCallback((user) => { // eslint-disable-line react-hooks/exhaustive-deps
    console.log('[MentionEditor] insertMention called with user:', user);
    
    if (!editorRef.current) {
      console.error('[MentionEditor] ❌ editorRef.current is null!');
      return;
    }
    
    // CRITICAL: Set flag to prevent handleInput from interfering
    isInsertingMentionRef.current = true;
    
    const selection = window.getSelection();
    if (!selection.rangeCount) {
      console.warn('[MentionEditor] ⚠️  No selection range found, attempting to create one');
      // Try to create a selection at the end of the editor
      const range = document.createRange();
      range.selectNodeContents(editorRef.current);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
      
      // Retry with new selection
      if (!selection.rangeCount) {
        console.error('[MentionEditor] ❌ Still no selection after attempt to create one');
        isInsertingMentionRef.current = false;
        return;
      }
    }
    
    const range = selection.getRangeAt(0);
    const textBeforeCursor = getTextBeforeCursor();
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    console.log('[MentionEditor] Insertion context:', {
      textBeforeCursor: textBeforeCursor.substring(Math.max(0, textBeforeCursor.length - 50)),
      lastAtIndex,
      rangeStartContainer: range.startContainer.nodeType === Node.TEXT_NODE ? range.startContainer.textContent.substring(0, 50) : 'Element',
      rangeStartOffset: range.startOffset
    });
    
    // Handle case where @ is not found - insert at current cursor or end
    if (lastAtIndex === -1) {
      console.warn('[MentionEditor] ⚠️  No @ found before cursor, inserting mention at current position');
      // Update range to current cursor position
      const range = document.createRange();
      if (range.startContainer && editorRef.current.contains(range.startContainer)) {
        // Use current range
      } else {
        // Fallback: select end of editor
        range.selectNodeContents(editorRef.current);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
    
    // Re-get range after potential updates
    const finalRange = selection.rangeCount > 0 ? selection.getRangeAt(0) : range;
    
    // Build complete user object with ALL fields - this is our source of truth
    // Extract name using same logic as dropdown to ensure consistency
    let extractedName = user.name || user.displayName || user.fullName;
    if (!extractedName && (user.firstName || user.lastName)) {
      extractedName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    }
    if (!extractedName) {
      extractedName = user.email ? user.email.split('@')[0] : 'User';
    }
    
    const completeUser = {
      id: user.id || user._id,
      _id: user._id || user.id,
      email: user.email, // CRITICAL: Email is unique identifier
      name: extractedName, // Use extracted name for consistency
      displayName: user.displayName || extractedName,
      fullName: user.fullName || extractedName,
      firstName: user.firstName,
      lastName: user.lastName,
      ...user // Include all other fields from API response
    };
    
    console.log('[MentionEditor] Inserting mention for user:', {
      email: completeUser.email,
      name: extractedName,
      displayName: user.displayName,
      fullName: user.fullName,
      firstName: user.firstName,
      lastName: user.lastName,
      id: completeUser.id || completeUser._id
    });
    
    // Use the extracted name for the chip
    const userName = extractedName;
    
    const chip = document.createElement('span');
    chip.className = 'mention-chip';
    chip.contentEditable = 'false';
    chip.setAttribute('data-user-id', completeUser.id || completeUser._id || '');
    chip.setAttribute('data-user-email', completeUser.email || ''); // CRITICAL: Store exact email
    chip.setAttribute('data-user-name', userName);
    chip.setAttribute('data-user-object', JSON.stringify(completeUser)); // Store full object as backup
    
    chip.textContent = `@${userName}`;
    chip.style.cssText = `
      display: inline-flex;
      align-items: center;
      background: #DBEAFE;
      color: #1E40AF;
      padding: 0.125rem 0.5rem;
      border-radius: 4px;
      font-weight: 600;
      font-size: 0.8125rem;
      margin: 0 0.125rem;
      border: 1px solid #BFDBFE;
      white-space: nowrap;
    `;
    
    // ULTRA-SIMPLE DIRECT REPLACEMENT APPROACH
    // Find the exact @query text pattern and replace it with chip in ONE operation
    const startContainer = finalRange.startContainer;
    const startOffset = finalRange.startOffset;
    
    // Get the query text - use ref FIRST (most reliable, persists even if state changes)
    // This is set when user types @ and shows dropdown
    let queryToReplace = currentMentionQueryRef.current || mentionQuery || '';
    
    // Fallback: try to extract from textBeforeCursor if ref and state are empty
    if (!queryToReplace && lastAtIndex !== -1 && textBeforeCursor) {
      queryToReplace = textBeforeCursor.substring(lastAtIndex + 1).trim().toLowerCase();
    }
    
    // Fallback 2: if still empty, try to find any @text pattern near cursor
    if (!queryToReplace && startContainer.nodeType === Node.TEXT_NODE) {
      const text = startContainer.textContent || '';
      const atPos = text.lastIndexOf('@', startOffset - 1);
      if (atPos !== -1) {
        // Extract text from @ to cursor
        const potentialQuery = text.substring(atPos + 1, startOffset).trim().toLowerCase();
        if (potentialQuery && !potentialQuery.match(/[^\w@.-]/)) {
          // Looks like a valid query (no spaces/special chars)
          queryToReplace = potentialQuery;
        }
      }
    }
    
    console.log('[MentionEditor] Query to replace:', queryToReplace || '(none found)');
    console.log('[MentionEditor] Ref query:', currentMentionQueryRef.current);
    console.log('[MentionEditor] State query:', mentionQuery);
    console.log('[MentionEditor] Text before cursor (last 30 chars):', textBeforeCursor.substring(Math.max(0, textBeforeCursor.length - 30)));
    
    // DIRECT TEXT REPLACEMENT: Search all text nodes and replace @query with chip
    let replaced = false;
    const insertRange = document.createRange();
    const space = document.createTextNode(' ');
    
    if (queryToReplace) {
      // Search ALL text nodes in the editor for the @query pattern
      const allTextNodes = [];
      const walker = document.createTreeWalker(
        editorRef.current,
        NodeFilter.SHOW_TEXT,
        null
      );
      
      let node;
      while ((node = walker.nextNode())) {
        if (node.parentElement && node.parentElement.classList.contains('mention-chip')) {
          continue;
        }
        allTextNodes.push(node);
      }
      
      // Find and replace the @query pattern
      for (const textNode of allTextNodes) {
        const text = textNode.textContent || '';
        const lowerText = text.toLowerCase();
        
        // Look for @query (case-insensitive)
        const queryPattern = '@' + queryToReplace;
        const atIndex = lowerText.indexOf(queryPattern.toLowerCase());
        
        if (atIndex !== -1) {
          // Found it! Replace this occurrence
          // CRITICAL: Only replace the exact @query pattern, preserve everything else
          const beforeText = text.substring(0, atIndex);
          let afterText = text.substring(atIndex + queryPattern.length);
          
          // Trim leading whitespace from afterText since we're adding a space after chip
          // This prevents double spaces and ensures clean separation
          afterText = afterText.trimStart();
          
          // Create replacement: beforeText + chip + space + afterText
          const fragment = document.createDocumentFragment();
          
          if (beforeText) {
            fragment.appendChild(document.createTextNode(beforeText));
          }
          
          // Clone the chip so we can insert it (chip content is immutable: @userName only)
          const chipClone = chip.cloneNode(true);
          // CRITICAL: Ensure chip content is locked and cannot be modified
          chipClone.contentEditable = 'false';
          chipClone.textContent = `@${userName}`; // Re-set to ensure it's correct
          fragment.appendChild(chipClone);
          
          // Add space after chip (before any following text)
          fragment.appendChild(space.cloneNode(true));
          
          // Add remaining text AFTER the chip (not inside it)
          if (afterText) {
            const afterTextNode = document.createTextNode(afterText);
            fragment.appendChild(afterTextNode);
          }
          
          // Replace the text node with the fragment
          if (textNode.parentNode) {
            textNode.parentNode.replaceChild(fragment, textNode);
            
            // VERIFY: Ensure chip content is correct and hasn't been modified
            requestAnimationFrame(() => {
              const allChips = Array.from(editorRef.current.querySelectorAll('.mention-chip'));
              allChips.forEach(c => {
                const expectedText = `@${c.getAttribute('data-user-name') || ''}`;
                if (c.textContent !== expectedText) {
                  console.warn(`[MentionEditor] ⚠️ Chip content mismatch! Expected: "${expectedText}", Got: "${c.textContent}". Fixing...`);
                  c.textContent = expectedText;
                  c.contentEditable = 'false'; // Re-lock it
                }
              });
            });
            
            // IMMEDIATELY clean up any duplicate chips for this user (synchronous, before cursor positioning)
            const allChipsNow = Array.from(editorRef.current.querySelectorAll('.mention-chip'));
            const userChips = allChipsNow.filter(c => 
              c.getAttribute('data-user-email') === completeUser.email
            );
            
            // If we have more than one chip for this user, keep only the first
            if (userChips.length > 1) {
              console.log(`[MentionEditor] 🧹 Removing ${userChips.length - 1} duplicate chip(s) for ${completeUser.email}`);
              for (let i = 1; i < userChips.length; i++) {
                const duplicate = userChips[i];
                const nextSibling = duplicate.nextSibling;
                
                // Remove the duplicate chip
                duplicate.remove();
                
                // If there's a trailing space after it, remove that too (clean up spacing)
                if (nextSibling && nextSibling.nodeType === Node.TEXT_NODE && nextSibling.textContent.trim() === '') {
                  nextSibling.remove();
                }
              }
            }
            
            // Set cursor after the chip
            // eslint-disable-next-line no-unused-vars
            const chipsAfter = Array.from(editorRef.current.querySelectorAll('.mention-chip'));
            chipsAfter.find(c => 
              c.getAttribute('data-user-email') === completeUser.email
            );
            
            // Now position cursor after the (potentially cleaned) chip
            const finalChips = Array.from(editorRef.current.querySelectorAll('.mention-chip'));
            const finalInsertedChip = finalChips.find(c => 
              c.getAttribute('data-user-email') === completeUser.email
            );
            
            if (finalInsertedChip) {
              insertRange.setStartAfter(finalInsertedChip);
              insertRange.collapse(true);
            } else {
              // Fallback: set after the last chip
              const lastChip = finalChips[finalChips.length - 1];
              if (lastChip) {
                insertRange.setStartAfter(lastChip);
                insertRange.collapse(true);
              } else {
                // Final fallback: end of editor
                insertRange.selectNodeContents(editorRef.current);
                insertRange.collapse(false);
              }
            }
            
            // Update selection
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(insertRange);
            
            replaced = true;
            console.log('[MentionEditor] ✅ Directly replaced @query with chip');
            
            // AGGRESSIVE CLEANUP: Remove any remaining @query patterns from ALL text nodes
            // This handles cases where the query appears in multiple text nodes
            requestAnimationFrame(() => {
              const cleanupWalker = document.createTreeWalker(
                editorRef.current,
                NodeFilter.SHOW_TEXT,
                null
              );
              
              let cleanupNode;
              while ((cleanupNode = cleanupWalker.nextNode())) {
                if (cleanupNode.parentElement && cleanupNode.parentElement.classList.contains('mention-chip')) {
                  continue;
                }
                
                const text = cleanupNode.textContent || '';
                const lowerText = text.toLowerCase();
                const queryPattern = '@' + queryToReplace.toLowerCase();
                
                if (lowerText.includes(queryPattern)) {
                  // Found remaining @query - remove it
                  const cleanedText = text.replace(new RegExp(queryPattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), '');
                  
                  if (cleanedText !== text) {
                    cleanupNode.textContent = cleanedText;
                    console.log(`[MentionEditor] 🧹 Cleaned remaining @${queryToReplace} from text node`);
                  }
                }
              }
            });
            
            break; // CRITICAL: Only replace first occurrence
          }
        }
      }
    }
    
    // If replacement didn't happen, insert at cursor (fallback)
    if (!replaced) {
      console.log('[MentionEditor] Direct replacement failed, inserting at cursor');
      insertRange.setStart(startContainer, startOffset);
      insertRange.collapse(true);
      insertRange.insertNode(chip);
      insertRange.setStartAfter(chip);
      insertRange.insertNode(space);
    }
    
    insertRange.collapse(false);
    
    // Update selection
    selection.removeAllRanges();
    selection.addRange(insertRange);
    
    // IMMEDIATE cleanup: Remove any remaining @query text patterns
    // This ensures no leftover @query text remains
    if (queryToReplace && replaced) {
      requestAnimationFrame(() => {
        const cleanupWalker = document.createTreeWalker(
          editorRef.current,
          NodeFilter.SHOW_TEXT,
          null
        );
        
        let cleanupNode;
        const toClean = [];
        while ((cleanupNode = cleanupWalker.nextNode())) {
          if (cleanupNode.parentElement && cleanupNode.parentElement.classList.contains('mention-chip')) {
            continue;
          }
          
          const text = cleanupNode.textContent || '';
          const lowerText = text.toLowerCase();
          const queryPattern = '@' + queryToReplace.toLowerCase();
          const atIndex = lowerText.indexOf(queryPattern);
          
          if (atIndex !== -1) {
            // Found @query - remove it (shouldn't happen after direct replacement, but safety check)
            const nextSibling = cleanupNode.nextSibling;
            if (!nextSibling || !nextSibling.classList?.contains('mention-chip')) {
              // Not followed by chip - remove it
              toClean.push({
                node: cleanupNode,
                start: atIndex,
                end: atIndex + queryPattern.length
              });
            }
          }
        }
        
        // Clean up any remaining @query
        toClean.forEach(({ node, start, end }) => {
          try {
            const text = node.textContent || '';
            const before = text.substring(0, start);
            const after = text.substring(end);
            
            if (before || after) {
              // Replace node with before + after (removing @query)
              if (before) {
                const beforeNode = document.createTextNode(before);
                node.parentNode.insertBefore(beforeNode, node);
              }
              if (after) {
                const afterNode = document.createTextNode(after);
                node.parentNode.insertBefore(afterNode, node.nextSibling || node);
              }
              node.remove();
              console.log('[MentionEditor] ✅ Cleaned up leftover @query text');
            } else {
              // Empty node - just remove it
              node.remove();
            }
          } catch (e) {
            console.warn('[MentionEditor] Cleanup error:', e);
          }
        });
      });
    }
    
    console.log('[MentionEditor] ✅ Chip insertion complete');
    
    // Clean up: Remove duplicate mention chips only
    setTimeout(() => {
      const allChips = Array.from(editorRef.current.querySelectorAll('.mention-chip'));
      const seenEmails = new Set();
      allChips.forEach(chip => {
        const email = chip.getAttribute('data-user-email');
        if (email && seenEmails.has(email)) {
          // Duplicate chip - remove it
          chip.remove();
          console.log('[MentionEditor] Removed duplicate chip for:', email);
        } else if (email) {
          seenEmails.add(email);
        }
      });
    }, 50); // Quick cleanup for duplicates only
    
    setShowMentions(false);
    setMentionSuggestions([]);
    setMentionPosition(null);
    
    // Clear the query ref after successful replacement (or failed attempt)
    if (replaced || !queryToReplace) {
      currentMentionQueryRef.current = '';
    }
    
    // CRITICAL: Add user to selectedMentions IMMEDIATELY (source of truth)
    // Use email as unique identifier to prevent duplicates
    // IMPORTANT: Use functional update to ensure we have latest state
    setSelectedMentions(prev => {
      // Check if this exact user (by email) is already selected
      const userEmail = completeUser.email?.toLowerCase();
      const existingIndex = prev.findIndex(m => (m?.email || '').toLowerCase() === userEmail);
      
      if (existingIndex !== -1) {
        console.log('[MentionEditor] User already in selectedMentions:', completeUser.email);
        // Clear the flag and notify parent
        isInsertingMentionRef.current = false;
        if (onMentionsChange) {
          onMentionsChange(prev);
        }
        return prev;
      }
      
      // Add the complete user object
      const updated = [...prev, completeUser];
      console.log('[MentionEditor] ✅ Added user to selectedMentions:', {
        email: completeUser.email,
        name: completeUser.name,
        totalMentions: updated.length,
        allEmails: updated.map(m => m?.email).filter(Boolean)
      });
      
      // CRITICAL: Notify parent IMMEDIATELY (synchronously) BEFORE allowing handleInput to run
      // This ensures parent gets the mention before input event clears it
      if (onMentionsChange) {
        console.log('[MentionEditor] Notifying parent with updated mentions:', updated.length, updated.map(m => ({ email: m.email, name: m.name })));
        // Call synchronously to ensure parent gets the data immediately
        onMentionsChange(updated);
      }
      
      // Update text content AFTER notifying parent
      // Use multiple delays to ensure DOM is fully updated
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const text = extractText();
          onChange(text);
          editorRef.current.focus();
          
          // Double-check: Extract mentions from DOM again to ensure chip is there
          setTimeout(() => {
            const chipsInDOM = Array.from(editorRef.current.querySelectorAll('.mention-chip'));
            const mentionsFromDOM = chipsInDOM.map(chip => {
              const userObjectJson = chip.getAttribute('data-user-object');
              if (userObjectJson) {
                try {
                  return JSON.parse(userObjectJson);
                } catch (e) {
                  console.warn('[MentionEditor] Failed to parse user object:', e);
                }
              }
              return null;
            }).filter(Boolean);
            
            console.log('[MentionEditor] DOM verification after insertion:', {
              chipsFound: chipsInDOM.length,
              mentionsExtracted: mentionsFromDOM.length,
              chipEmails: mentionsFromDOM.map(m => m.email)
            });
            
            if (mentionsFromDOM.length > 0) {
              console.log('[MentionEditor] Re-verifying mentions from DOM after insertion:', mentionsFromDOM.length);
              // Use immediate notification here since this is part of mention insertion flow
              if (onMentionsChange) {
                onMentionsChange(mentionsFromDOM);
              }
            } else if (mentionsFromDOM.length === 0 && updated.length > 0) {
              console.error('[MentionEditor] ⚠️  Chip not found in DOM! Re-notifying with state:', updated.length);
              // Chip not in DOM yet - re-notify with state (immediate for insertion flow)
              if (onMentionsChange) {
                onMentionsChange(updated);
              }
            }
            
            // Clear flag after ensuring mentions are in DOM
            isInsertingMentionRef.current = false;
          }, 200);
        });
      });
      
      return updated;
    });
  }, [extractText, onChange, onMentionsChange]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleKeyDown = (e) => {
    if (showMentions && mentionSuggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedMentionIndex(prev => 
          prev < mentionSuggestions.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedMentionIndex(prev => prev > 0 ? prev - 1 : 0);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        if (mentionSuggestions[selectedMentionIndex]) {
          insertMention(mentionSuggestions[selectedMentionIndex]);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentions(false);
      }
    }
    
    // Handle backspace to delete mention chips
    if (e.key === 'Backspace') {
      const selection = window.getSelection();
      if (!selection.rangeCount) return;
      
      const range = selection.getRangeAt(0);
      if (range.startOffset === 0 && range.startContainer.previousSibling) {
        const prevNode = range.startContainer.previousSibling;
        if (prevNode.nodeType === Node.ELEMENT_NODE && prevNode.classList.contains('mention-chip')) {
          e.preventDefault();
          // Remove the mention from selectedMentions by email
          const deletedEmail = prevNode.getAttribute('data-user-email');
          prevNode.remove();
          
          setSelectedMentions(prev => {
            const filtered = prev.filter(m => (m.email || '').toLowerCase() !== (deletedEmail || '').toLowerCase());
            console.log('[MentionEditor] Removed mention:', {
              deletedEmail,
              remainingCount: filtered.length,
              remainingEmails: filtered.map(m => m.email)
            });
            notifyMentionsChange(filtered);
            return filtered;
          });
          
          const text = extractText();
          onChange(text);
        }
      }
    }
  };

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        editorRef.current &&
        !editorRef.current.contains(event.target)
      ) {
        setShowMentions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync value prop to contentEditable (for external updates)
  useEffect(() => {
    if (editorRef.current && value !== undefined) {
      const currentText = extractText();
      if (currentText !== value) {
        // Only update if different to avoid cursor jumps
        // This is mainly for clearing after submit
        if (value === '') {
          editorRef.current.innerHTML = '';
          setSelectedMentions([]);
          notifyMentionsChange([]);
        }
      }
    }
  }, [value, extractText, notifyMentionsChange]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (mentionChangeTimeoutRef.current) {
        clearTimeout(mentionChangeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div style={{ position: 'relative' }}>
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        style={{
          minHeight: '80px',
          maxHeight: '200px',
          padding: '0.75rem',
          border: '1px solid #D1D5DB',
          borderRadius: '6px',
          fontSize: '0.875rem',
          fontFamily: 'inherit',
          outline: 'none',
          overflowY: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          lineHeight: '1.6'
        }}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />
      <style>{`
        [contenteditable][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: #9CA3AF;
          pointer-events: none;
        }
        .mention-chip {
          user-select: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
        }
      `}</style>
      
      {/* Mention Suggestions Dropdown */}
      {showMentions && mentionSuggestions.length > 0 && (
        <div
          ref={dropdownRef}
          style={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            right: 0,
            marginBottom: '0.5rem',
            background: 'white',
            border: '1px solid #3B82F6',
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(59, 130, 246, 0.2)',
            maxHeight: '240px',
            overflowY: 'auto',
            zIndex: 1000
          }}
        >
          <div style={{
            padding: '0.5rem 0.75rem',
            background: '#EFF6FF',
            borderBottom: '1px solid #DBEAFE',
            fontSize: '0.75rem',
            fontWeight: '600',
            color: '#1E40AF',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            Select User to Mention
          </div>
          {mentionSuggestions.map((user, index) => {
            // Extract full name - prioritize name/displayName, fallback to firstName+lastName, then email
            let userName = user.name || user.displayName || user.fullName;
            if (!userName && (user.firstName || user.lastName)) {
              userName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
            }
            if (!userName) {
              userName = user.email.split('@')[0];
            }
            const userEmail = user.email;
            
            return (
              <div
                key={user.id || user._id || user.email}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('[MentionEditor] User clicked from dropdown:', {
                    email: userEmail,
                    name: userName,
                    id: user.id || user._id
                  });
                  // Insert mention immediately - this will set the flag and notify parent
                  insertMention(user);
                  // Close dropdown after insertion
                  setTimeout(() => {
                    setShowMentions(false);
                  }, 100);
                }}
                onMouseEnter={() => setSelectedMentionIndex(index)}
                style={{
                  padding: '0.875rem',
                  cursor: 'pointer',
                  background: index === selectedMentionIndex ? '#EFF6FF' : 'white',
                  borderLeft: index === selectedMentionIndex ? '3px solid #3B82F6' : '3px solid transparent',
                  borderBottom: index < mentionSuggestions.length - 1 ? '1px solid #F3F4F6' : 'none'
                }}
              >
                <div style={{ 
                  fontSize: '0.875rem', 
                  fontWeight: '600',
                  color: '#111827',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: '#3B82F6',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    flexShrink: 0
                  }}>
                    {(userName || userEmail || '?')[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: '600', color: '#111827' }}>
                      {userName}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.125rem' }}>
                      {userEmail}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MentionEditor;
